import { useState, useRef, useCallback } from 'react';
import { IMediaRecorder, MediaRecorder, register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';

export interface AudioRecording {
  blob: Blob;
  url: string;
  duration: number;
  sentenceIndex: number;
}

export interface EchoReaderAudioState {
  isRecording: boolean;
  currentRecording: AudioRecording | null;
  recordings: AudioRecording[];
  error: string | null;
  isInitialized: boolean;
}

export function useEchoReaderAudio() {
  const [state, setState] = useState<EchoReaderAudioState>({
    isRecording: false,
    currentRecording: null,
    recordings: [],
    error: null,
    isInitialized: false,
  });

  const mediaRecorderRef = useRef<IMediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);

  // Initialize the audio system
  const initializeAudio = useCallback(async () => {
    try {
      // Register WAV encoder for better audio quality
      try {
        await register(await connect());
      } catch (e) {
        console.log('WAV encoder registration skipped:', e);
      }

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
        }
      });
      
      // Stop the stream immediately - we'll create new ones for each recording
      stream.getTracks().forEach(track => track.stop());
      
      setState(prev => ({ 
        ...prev, 
        isInitialized: true, 
        error: null 
      }));
      
      return true;
    } catch (error) {
      console.error('Failed to initialize audio:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to access microphone. Please check your permissions.',
        isInitialized: false 
      }));
      return false;
    }
  }, []);

  // Start recording for a specific sentence
  const startRecording = useCallback(async (sentenceIndex: number) => {
    if (state.isRecording) {
      console.warn('Already recording');
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
        }
      });

      const mediaRecorder = new MediaRecorder(stream, { 
        mimeType: 'audio/wav',
        audioBitsPerSecond: 128000,
      });

      mediaRecorderRef.current = mediaRecorder;
      streamRef.current = stream;
      audioChunksRef.current = [];
      startTimeRef.current = Date.now();

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const endTime = Date.now();
        const duration = (endTime - startTimeRef.current) / 1000; // Duration in seconds
        
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        const recording: AudioRecording = {
          blob: audioBlob,
          url: audioUrl,
          duration,
          sentenceIndex,
        };

        setState(prev => ({
          ...prev,
          isRecording: false,
          currentRecording: recording,
          recordings: [...prev.recordings.filter(r => r.sentenceIndex !== sentenceIndex), recording],
          error: null,
        }));

        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('Recording error:', event);
        setState(prev => ({
          ...prev,
          isRecording: false,
          error: 'Recording failed. Please try again.',
        }));
        
        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setState(prev => ({ 
        ...prev, 
        isRecording: true, 
        error: null 
      }));
      
      return true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to start recording. Please check your microphone.',
        isRecording: false 
      }));
      return false;
    }
  }, [state.isRecording]);

  // Stop the current recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording) {
      mediaRecorderRef.current.stop();
    }
  }, [state.isRecording]);

  // Cancel the current recording
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording) {
      setState(prev => ({ ...prev, isRecording: false }));
      
      // Stop the media recorder without saving
      mediaRecorderRef.current.stop();
      
      // Clean up stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Clear audio chunks
      audioChunksRef.current = [];
    }
  }, [state.isRecording]);

  // Get recording for a specific sentence
  const getRecording = useCallback((sentenceIndex: number): AudioRecording | null => {
    return state.recordings.find(r => r.sentenceIndex === sentenceIndex) || null;
  }, [state.recordings]);

  // Delete recording for a specific sentence
  const deleteRecording = useCallback((sentenceIndex: number) => {
    setState(prev => {
      const recordingToDelete = prev.recordings.find(r => r.sentenceIndex === sentenceIndex);
      if (recordingToDelete) {
        // Revoke the object URL to free memory
        URL.revokeObjectURL(recordingToDelete.url);
      }
      
      return {
        ...prev,
        recordings: prev.recordings.filter(r => r.sentenceIndex !== sentenceIndex),
        currentRecording: prev.currentRecording?.sentenceIndex === sentenceIndex ? null : prev.currentRecording,
      };
    });
  }, []);

  // Clear all recordings
  const clearAllRecordings = useCallback(() => {
    setState(prev => {
      // Revoke all object URLs to free memory
      prev.recordings.forEach(recording => {
        URL.revokeObjectURL(recording.url);
      });
      
      if (prev.currentRecording) {
        URL.revokeObjectURL(prev.currentRecording.url);
      }
      
      return {
        ...prev,
        recordings: [],
        currentRecording: null,
      };
    });
  }, []);

  // Play a recording
  const playRecording = useCallback((sentenceIndex: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      const recording = getRecording(sentenceIndex);
      if (!recording) {
        reject(new Error('No recording found for this sentence'));
        return;
      }

      const audio = new Audio(recording.url);
      audio.onended = () => resolve();
      audio.onerror = () => reject(new Error('Failed to play recording'));
      audio.play().catch(reject);
    });
  }, [getRecording]);

  // Get total duration of all recordings
  const getTotalDuration = useCallback((): number => {
    return state.recordings.reduce((total, recording) => total + recording.duration, 0);
  }, [state.recordings]);

  // Check if microphone is supported
  const isMicrophoneSupported = useCallback((): boolean => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }, []);

  return {
    // State
    ...state,
    
    // Actions
    initializeAudio,
    startRecording,
    stopRecording,
    cancelRecording,
    getRecording,
    deleteRecording,
    clearAllRecordings,
    playRecording,
    
    // Utilities
    getTotalDuration,
    isMicrophoneSupported,
  };
}